import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Check, X, Lock, Building2, AlertCircle, Globe, Globe2, ChevronRight, ArrowLeft, MapPin, Sparkles, CalendarClock,
} from 'lucide-react';
import { JOB_BOARDS, BOARD_META } from '../constants/jobBoards';
import {
  CONTINENTS, GLOBAL_BOARDS, boardsForCountry, localBoardCount,
  regionLocalBoardCount, continentLocalBoardCount,
} from '../constants/geoBoards';
import BrandLogos from './BrandLogos';

const BOARD_BY_ID = Object.fromEntries(JOB_BOARDS.map((b) => [b.id, b]));
const DAYS_OPTIONS = [30, 90, 120];
const DEFAULT_DAYS = 90;

/**
 * Geographic drill-down publisher:  Continent → Sub-region → Country → Boards.
 * Selection accumulates across countries and is deduped by board id.
 * onConfirm(selectedIds, daysByBoard) — daysByBoard = { boardId: activeDays }.
 * Sector auto-match suggests boards from the role's industry + location.
 */
const GeoPublishModal = ({ open, defaultSelected = [], organizationId, roleIndustry = '', roleLocation = '', loading = false, onCancel, onConfirm }) => {
  const [selected, setSelected] = useState(defaultSelected);
  const [daysByBoard, setDaysByBoard] = useState({});
  const [connectionByKey, setConnectionByKey] = useState({});
  const [level, setLevel] = useState('continents'); // continents | regions | countries | boards
  const [activeContinent, setActiveContinent] = useState(null);
  const [activeRegion, setActiveRegion] = useState(null);
  const [activeCountry, setActiveCountry] = useState(null); // { code, name } — code 'GLOBAL' = worldwide

  useEffect(() => {
    if (open) {
      setSelected(defaultSelected);
      setDaysByBoard(Object.fromEntries((defaultSelected || []).map((id) => [id, DEFAULT_DAYS])));
      setLevel('continents');
      setActiveContinent(null);
      setActiveRegion(null);
      setActiveCountry(null);
    }
  }, [open, defaultSelected]);

  useEffect(() => {
    if (!open || !organizationId) return;
    const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    axios
      .get(`${base}/api/job-boards/catalog?organizationId=${organizationId}`)
      .then((r) => {
        const map = {};
        (r.data?.data || []).forEach((b) => { map[b.key] = b.connectionState; });
        setConnectionByKey(map);
      })
      .catch(() => setConnectionByKey({}));
  }, [open, organizationId]);

  const recommended = useMemo(() => {
    const ind = (roleIndustry || '').toLowerCase().trim();
    const loc = (roleLocation || '').toLowerCase().trim();
    const avail = (b) => b.scope === 'platform' || (b.scope === 'organization' && connectionByKey[b.id] === 'connected');
    if (!ind && !loc) return [];
    return JOB_BOARDS
      .map((b) => {
        const meta = BOARD_META[b.id] || { sectors: [], strongCountries: [] };
        let score = 0;
        if (ind && meta.sectors.some((s) => { const sl = s.toLowerCase(); return ind.includes(sl) || sl.includes(ind); })) score += 2;
        if (loc && meta.strongCountries.some((c) => { const cl = c.toLowerCase(); return loc.includes(cl) || cl.includes(loc); })) score += 2;
        if (avail(b)) score += 1;
        return { b, score, avail: avail(b) };
      })
      .filter((x) => x.score >= 2 && x.avail)
      .sort((a, b) => b.score - a.score)
      .slice(0, 5)
      .map((x) => x.b);
  }, [roleIndustry, roleLocation, connectionByKey]);

  if (!open) return null;

  const isAvailable = (board) => {
    if (board.scope === 'platform') return true;
    if (board.scope === 'organization') return connectionByKey[board.id] === 'connected';
    return false;
  };

  const setDays = (boardId, days) => setDaysByBoard((d) => ({ ...d, [boardId]: days }));

  const removeBoard = (boardId) => {
    setSelected((prev) => prev.filter((x) => x !== boardId));
    setDaysByBoard((d) => { const n = { ...d }; delete n[boardId]; return n; });
  };

  const addBoard = (board) => {
    if (!isAvailable(board) || selected.includes(board.id)) return;
    setSelected((prev) => [...prev, board.id]);
    setDaysByBoard((d) => ({ ...d, [board.id]: d[board.id] || DEFAULT_DAYS }));
  };

  const toggle = (board) => {
    if (!isAvailable(board)) return;
    if (selected.includes(board.id)) removeBoard(board.id);
    else addBoard(board);
  };

  const currentBoards = () =>
    activeCountry?.code === 'GLOBAL' ? GLOBAL_BOARDS : boardsForCountry(activeCountry?.code);

  const selectAllInCountry = () => {
    const ids = currentBoards().filter(isAvailable).map((b) => b.id);
    setSelected((prev) => Array.from(new Set([...prev, ...ids])));
    setDaysByBoard((d) => { const n = { ...d }; ids.forEach((id) => { if (!n[id]) n[id] = DEFAULT_DAYS; }); return n; });
  };

  const goContinents = () => { setLevel('continents'); setActiveContinent(null); setActiveRegion(null); setActiveCountry(null); };
  const goRegions = () => { setLevel('regions'); setActiveRegion(null); setActiveCountry(null); };
  const goCountries = () => { setLevel('countries'); setActiveCountry(null); };

  // ── Card renderers ──────────────────────────────────────────────
  const MapCard = ({ title, subtitle, onClick, testid, icon: Icon = Globe2 }) => (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className="group relative text-left rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 hover:from-blue-50 hover:to-blue-100 hover:border-blue-300 p-4 transition-all shadow-sm hover:shadow"
    >
      <div className="flex items-start justify-between">
        <span className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center text-slate-500 group-hover:text-blue-600 transition-colors">
          <Icon className="w-5 h-5" />
        </span>
        <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 transition-colors mt-3" />
      </div>
      <h3 className="mt-3 font-semibold text-slate-900 text-sm leading-tight">{title}</h3>
      {subtitle && <p className="text-[11px] text-slate-500 mt-0.5">{subtitle}</p>}
    </button>
  );

  const BoardCard = ({ b }) => {
    const isOn = selected.includes(b.id);
    const available = isAvailable(b);
    const badge =
      b.scope === 'platform'
        ? { icon: Check, text: 'Available', cls: 'bg-green-50 text-green-700' }
        : b.scope === 'organization'
        ? available
          ? { icon: Building2, text: 'via your account', cls: 'bg-green-50 text-green-700' }
          : { icon: Lock, text: 'Connect first', cls: 'bg-amber-50 text-amber-700' }
        : { icon: AlertCircle, text: 'Coming soon', cls: 'bg-gray-100 text-gray-500' };
    const BadgeIcon = badge.icon;
    return (
      <div
        data-testid={`geo-board-card-${b.id}`}
        className={`relative bg-white rounded-xl border-2 p-4 transition-all ${
          !available ? 'border-gray-200 opacity-60'
          : isOn ? 'border-blue-500 shadow-md' : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }`}
      >
        <button
          type="button"
          onClick={() => toggle(b)}
          disabled={!available}
          aria-pressed={isOn}
          className={`text-left w-full ${available ? 'cursor-pointer' : 'cursor-not-allowed'}`}
        >
          {isOn && available && (
            <span className="absolute top-3 right-3 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
              <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
            </span>
          )}
          <div className="flex items-center gap-3 mb-3">
            {b.inlineGlyph && BrandLogos[b.inlineGlyph] ? (
              React.createElement(BrandLogos[b.inlineGlyph], { size: 36 })
            ) : b.logoUrl ? (
              <img src={b.logoUrl} alt={b.name} className="w-9 h-9 object-contain" loading="lazy" />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: b.color }}>
                {b.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 text-sm leading-tight">{b.name}</h3>
              <p className="text-[11px] text-gray-500 mt-0.5 flex items-center gap-1"><Globe className="w-3 h-3" /> {b.country}</p>
            </div>
          </div>
          <p className="text-xs text-gray-600 leading-snug mb-2 line-clamp-2">{b.description}</p>
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${badge.cls}`}>
            <BadgeIcon className="w-3 h-3" /> {badge.text}
          </span>
        </button>
        {isOn && available && (
          <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2" data-testid={`geo-days-${b.id}`}>
            <CalendarClock className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-[11px] text-slate-500">Active for</span>
            <select
              value={daysByBoard[b.id] || DEFAULT_DAYS}
              onChange={(e) => setDays(b.id, Number(e.target.value))}
              className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
              data-testid={`geo-days-select-${b.id}`}
            >
              {DAYS_OPTIONS.map((d) => <option key={d} value={d}>{d} days</option>)}
            </select>
          </div>
        )}
      </div>
    );
  };

  // ── Breadcrumb ──────────────────────────────────────────────────
  const Crumb = ({ label, onClick, active }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={active}
      className={`text-sm ${active ? 'text-slate-900 font-semibold cursor-default' : 'text-blue-600 hover:text-blue-700'}`}
    >
      {label}
    </button>
  );

  const breadcrumbs = (
    <div className="flex items-center gap-1.5 flex-wrap text-slate-400" data-testid="geo-breadcrumb">
      <Crumb label="World" onClick={goContinents} active={level === 'continents'} />
      {activeContinent && (<><ChevronRight className="w-3.5 h-3.5" /><Crumb label={activeContinent.name} onClick={goRegions} active={level === 'regions'} /></>)}
      {activeRegion && (<><ChevronRight className="w-3.5 h-3.5" /><Crumb label={activeRegion.name} onClick={goCountries} active={level === 'countries'} /></>)}
      {activeCountry && (<><ChevronRight className="w-3.5 h-3.5" /><span className="text-sm text-slate-900 font-semibold">{activeCountry.name}</span></>)}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onCancel} data-testid="geo-publish-overlay">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="geo-publish-modal">
        {/* Header */}
        <div className="px-7 py-5 border-b border-gray-100 flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Publish to job boards</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Choose a region, then pick the boards that operate there.
              {' '}<span className="font-semibold text-blue-600" data-testid="geo-selected-count">{selected.length} selected</span>.
            </p>
          </div>
          <button onClick={onCancel} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="geo-publish-close" aria-label="Close">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Sub-header: back + breadcrumb */}
        <div className="px-7 pt-4 pb-2 flex items-center gap-3">
          {level !== 'continents' && (
            <button
              onClick={level === 'regions' ? goContinents : level === 'countries' ? goRegions : goCountries}
              className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
              data-testid="geo-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          )}
          {breadcrumbs}
        </div>

        {/* Body */}
        <div className="px-7 pb-4 overflow-y-auto flex-1">
          {level === 'continents' && recommended.length > 0 && (
            <div className="mb-5 rounded-xl border border-indigo-100 bg-indigo-50/50 p-4" data-testid="geo-recommended">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-indigo-500" />
                <p className="text-sm font-semibold text-slate-800">Recommended for this role</p>
                <span className="text-[11px] text-slate-500">based on its sector &amp; location</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {recommended.map((b) => {
                  const on = selected.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => (on ? removeBoard(b.id) : addBoard(b))}
                      data-testid={`geo-recommended-${b.id}`}
                      className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-full border transition-colors ${
                        on ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-slate-200 text-slate-700 hover:border-blue-300'
                      }`}
                    >
                      {on ? <Check className="w-3.5 h-3.5" /> : <span className="text-indigo-400 font-bold">+</span>}
                      {b.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {level === 'continents' && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="geo-continents">
              <MapCard
                title="Worldwide"
                subtitle={`${GLOBAL_BOARDS.length} global boards`}
                icon={Globe}
                testid="geo-continent-worldwide"
                onClick={() => { setActiveContinent(null); setActiveRegion(null); setActiveCountry({ code: 'GLOBAL', name: 'Worldwide' }); setLevel('boards'); }}
              />
              {CONTINENTS.map((c) => {
                const cnt = continentLocalBoardCount(c);
                return (
                  <MapCard
                    key={c.id}
                    title={c.name}
                    subtitle={`${c.regions.length} regions · ${cnt} local board${cnt === 1 ? '' : 's'}`}
                    testid={`geo-continent-${c.id}`}
                    onClick={() => { setActiveContinent(c); setLevel('regions'); }}
                  />
                );
              })}
            </div>
          )}

          {level === 'regions' && activeContinent && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="geo-regions">
              {activeContinent.regions.map((r) => {
                const cnt = regionLocalBoardCount(r);
                return (
                  <MapCard
                    key={r.id}
                    title={r.name}
                    subtitle={`${r.countries.length} countries · ${cnt} local board${cnt === 1 ? '' : 's'}`}
                    icon={MapPin}
                    testid={`geo-region-${r.id}`}
                    onClick={() => { setActiveRegion(r); setLevel('countries'); }}
                  />
                );
              })}
            </div>
          )}

          {level === 'countries' && activeRegion && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="geo-countries">
              {activeRegion.countries.map((c) => {
                const total = boardsForCountry(c.code).length;
                const local = localBoardCount(c.code);
                return (
                  <MapCard
                    key={c.code}
                    title={c.name}
                    subtitle={local > 0 ? `${total} boards (${local} local)` : `${total} global boards`}
                    icon={MapPin}
                    testid={`geo-country-${c.code}`}
                    onClick={() => { setActiveCountry(c); setLevel('boards'); }}
                  />
                );
              })}
            </div>
          )}

          {level === 'boards' && activeCountry && (
            <div data-testid="geo-boards">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm text-slate-500">
                  Boards operating in <span className="font-semibold text-slate-800">{activeCountry.name}</span>
                </p>
                <button onClick={selectAllInCountry} className="text-sm text-blue-600 hover:text-blue-700 font-medium" data-testid="geo-select-all-country">
                  Select all available
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {currentBoards().map((b) => <BoardCard key={b.id} b={b} />)}
              </div>
            </div>
          )}
        </div>

        {/* Selected chips */}
        {selected.length > 0 && (
          <div className="px-7 py-3 border-t border-gray-100 bg-slate-50" data-testid="geo-selected-chips">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Selected:</span>
              {selected.map((id) => {
                const b = BOARD_BY_ID[id];
                if (!b) return null;
                return (
                  <span key={id} className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 text-xs px-2 py-1 rounded-full">
                    {b.name}
                    <span className="text-[10px] text-slate-400">· {daysByBoard[id] || DEFAULT_DAYS}d</span>
                    <button onClick={() => removeBoard(id)} className="hover:text-red-500" data-testid={`geo-chip-remove-${id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-7 py-4 border-t border-gray-100 flex items-center justify-between bg-white">
          <p className="text-xs text-gray-500">Connect more boards in <strong>Settings → Job boards</strong>.</p>
          <div className="flex items-center gap-3">
            <button onClick={onCancel} className="px-5 py-2.5 text-sm font-medium text-gray-700 hover:text-gray-900 rounded-lg" data-testid="geo-publish-cancel">Cancel</button>
            <button
              onClick={() => onConfirm(selected, daysByBoard)}
              disabled={loading}
              className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm font-semibold rounded-lg"
              data-testid="geo-publish-confirm"
            >
              {loading ? 'Publishing…' : `Publish${selected.length ? ` to ${selected.length}` : ''}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeoPublishModal;
